/** 阅读卡提醒参数共用表单，App 默认与语言配置共用边界和业务说明。 */
import {
  ReadingPassFreeTrialRemindMaxCountSchema,
  ReadingPassFreeTrialRemindTimeSchema,
} from '@novel-admin/shared';
import { AdminApiError } from '~/api/errors';
import { FormRow } from '~/ui/FormRow';
import { Switch } from '~/ui/Switch';

export interface ReadingPassReminderDraft {
  enabled: boolean;
  time: string;
  maxCount: string;
}

/** 提醒关闭时仍须保存合法参数，避免再开启时依赖无效的历史值。 */
export function readingPassReminderIssue(draft: ReadingPassReminderDraft): string | null {
  if (!ReadingPassFreeTrialRemindTimeSchema.safeParse(draft.time).success)
    return '提醒时间须为 HH:MM，且在 07:00–21:00（含边界）';
  if (!ReadingPassFreeTrialRemindMaxCountSchema.safeParse(Number(draft.maxCount)).success)
    return '提醒次数上限须为 1–10 的整数';
  return null;
}

/** 字段错误与缺少支付方式详情都保留在当前表单中，便于运营逐项排查。 */
export function formatReadingPassError(error: unknown): string {
  const message = error instanceof Error ? error.message : '操作失败';
  if (!(error instanceof AdminApiError)) return message;
  const details = error.details;
  const missingMethods = Array.isArray(details?.missing_payment_methods)
    ? details.missing_payment_methods.filter((method) => typeof method === 'string')
    : [];
  return [
    message,
    details?.no_enabled_channel === true ? '尚未启用支付通道，请先配置全局支付通道策略' : '',
    missingMethods.length ? `未就绪的支付方式：${missingMethods.join('、')}` : '',
    typeof details?.region === 'string' ? `地区：${details.region}` : '',
  ]
    .filter(Boolean)
    .join(' · ');
}

export function ReadingPassReminderFields({
  draft,
  onChange,
  disabled,
  statusUnknown = false,
}: {
  draft: ReadingPassReminderDraft;
  onChange: (patch: Partial<ReadingPassReminderDraft>) => void;
  disabled: boolean;
  /** 服务端未返回开关真值（部署窗口旧 Worker）：不能把缺省值当成库里的状态展示。 */
  statusUnknown?: boolean;
}) {
  return (
    <>
      <FormRow
        label="免费体验卡领取提醒"
        hint="提醒尚未领取或已领取但未激活的用户，不会重新发放已领取的体验卡。"
      >
        <div className="hstack" style={{ gap: 10, flexWrap: 'wrap' }}>
          <Switch
            ariaLabel="免费体验卡领取提醒"
            checked={draft.enabled}
            disabled={disabled}
            onChange={(enabled) => onChange({ enabled })}
          />
          {statusUnknown ? (
            <span className="chip">提醒状态未知</span>
          ) : (
            <span className={`chip ${draft.enabled ? 'chip-pos' : ''}`}>
              {draft.enabled ? '提醒已开启' : '提醒已关闭'}
            </span>
          )}
        </div>
      </FormRow>
      <FormRow
        label="提醒时间"
        hint={
          <span>
            按用户 <span className="mono">day_cut_timezone</span> 的本地钟点解释，范围{' '}
            <span className="mono">07:00–21:00</span>（含边界）。
          </span>
        }
      >
        <input
          aria-label="提醒时间"
          className="input mono"
          value={draft.time}
          placeholder="HH:MM"
          disabled={disabled}
          onChange={(event) => onChange({ time: event.target.value })}
          style={{ width: 120, maxWidth: '100%' }}
        />
      </FormRow>
      <FormRow
        label="提醒次数上限"
        hint="同一用户、同一 App 跨语言和配置变更累计逻辑提醒次数，计数先于实际投递，不按天重置；范围 1–10 次。"
      >
        <div className="hstack" style={{ gap: 8, flexWrap: 'wrap' }}>
          <input
            aria-label="提醒次数上限"
            className="input mono"
            inputMode="numeric"
            value={draft.maxCount}
            disabled={disabled}
            onChange={(event) => onChange({ maxCount: event.target.value })}
            style={{ width: 100, maxWidth: '100%', textAlign: 'right' }}
          />
          <span style={{ color: 'var(--fg-muted)', fontSize: 12 }}>次</span>
        </div>
      </FormRow>
    </>
  );
}
